import React, { useState, useEffect } from 'react';
import AppPaginator from '../../AppPaginator';
import AppSearchBox from '../../AppSearchBox';
import AppBreadCrumb from '../../common/AppBreadCrumb';
import PerPageBlock from '../../common/PerPageBlock';
import AppToaster from '../../AppToaster';
import { getCreditNotes, destroyCreditNote } from '../../../api/CreditNotesAPI';
import { handleApiError } from '../../../api/risapi';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { format, parseISO } from 'date-fns';

const CreditNotesPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const successMessage = location.state?.message;
    const [entityName, setEntityName] = useState('Credit Note');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewDataLoading, setViewDataLoading] = useState(false);
    const [searchTermCompleted, setSearchTermCompleted] = useState('');
    const [perPage, setPerPage] = useState(process.env.REACT_APP_PER_PAGE_COUNT || 10);
    const [notesList, setNotesList] = useState({ paginate: true, data: [], per_page: perPage, current_page: 1, tot_page: null });
    const [toaster, setToaster] = useState({});
    const [showToaster, setShowToaster] = useState(false);
    const [saveCompleted, setSaveCompleted] = useState(false);

    const handleAddClick = () => navigate('/franchisee_user/sales/credit-notes/create');
    const handleEditClick = (id) => navigate(`/franchisee_user/sales/credit-notes/edit/${id}`);

    const handleDeleteClick = (id) => {
        Swal.fire({
            title: 'Are you sure?', text: "This will permanently delete the Credit Note and reverse its financial entry.", icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', confirmButtonText: 'Yes, delete it!'
        }).then((result) => {
            if (result.isConfirmed) {
                destroyCreditNote(id, (res) => {
                    if (res.success) {
                        setToaster({ type: 'success', message: 'Credit Note deleted.' }); setShowToaster(true); setSaveCompleted(prev => !prev);
                    } else {
                        handleApiError(res, () => {}); setToaster({ type: 'danger', message: res.error?.response?.data?.message || 'Delete failed.' }); setShowToaster(true);
                    }
                });
            }
        });
    };

    useEffect(() => {
         if (successMessage) { setShowToaster(true); setToaster({ type: 'success', message: successMessage }); navigate(location.pathname, { replace: true, state: {} }); }
     }, [successMessage, location.pathname, navigate]);

    useEffect(() => {
         let filters = { paginate: true, page: currentPage, per_page: perPage, search_term: searchTermCompleted };
         setViewDataLoading(true);
         getCreditNotes(filters, (res) => {
             setViewDataLoading(false);
             if (res.credit_notes) {
                 setNotesList({ paginate: true, per_page: res.credit_notes.per_page, data: res.credit_notes.data, current_page: res.credit_notes.current_page, tot_page: res.credit_notes.last_page });
             } else { handleApiError(res); }
         });
     }, [currentPage, searchTermCompleted, perPage, saveCompleted]);

    const formatDate = (d) => { try { return format(parseISO(d), 'dd-MMM-yyyy'); } catch (e) { return d; } };

    return (
        <>
            <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
            <main className="container-fluid">
                <AppBreadCrumb title='Credit Notes List' />
                <div className="row bg-white">
                    <div className="col-12">
                        <div className="card">
                           <div className="card-header d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
    <h5 className="mb-0">All Credit Notes</h5>
    
    {/* Container to handle the search box and button alignment */}
    <div className="d-flex flex-column flex-sm-row gap-2 w-100 w-md-auto">
        <div className="flex-grow-1">
            <AppSearchBox searchTerm={searchTerm} setSearchTerm={setSearchTerm} setSearchTermCompleted={setSearchTermCompleted} />
        </div>
        <button 
            className="btn btn-red btn-sm text-nowrap" 
            onClick={handleAddClick}
        >
            <i className="ph-bold ph-circles-three-plus"></i> Add Credit Note
        </button>
    </div>
</div>
                            <div className="card-body p-0">
                                {/* Added: Top Paginator/PerPage block for consistency if you have many records */}
                                <div className="d-flex flex-wrap m-3">
                                    {notesList.data.length > 0 && (
                                        <>
                                            <AppPaginator entityName={entityName} currentPage={notesList.current_page} totalPages={notesList.tot_page} setCurrentPage={setCurrentPage} />
                                            <PerPageBlock perPage={perPage} setPerPage={setPerPage} />
                                        </>
                                    )}
                                    {viewDataLoading && <div className="loader_box ms-auto"><div className="loader_14"></div></div>}
                                </div>

                                <div className="row p-3">
                                    {!viewDataLoading && notesList.data.map((cn) => (
                                        <div className="col-md-6 col-xl-4 mb-3" key={cn.id}>
                                            <div className="card h-100 border hover-effect">
                                                <div className="card-body">
                                                    <div className="d-flex justify-content-between mb-2">
                                                        <h6 className="fw-bold mb-0">{cn.party?.name}</h6>
                                                        <span className="badge bg-light text-dark">{cn.credit_note_number}</span>
                                                    </div>
                                                    <div className="text-muted small mb-3">{formatDate(cn.credit_note_date)}</div>
                                                    <div className="d-flex justify-content-between align-items-end">
                                                        <div><small>Total Amount</small><h5 className="text-primary mb-0">₹{parseFloat(cn.total_amount).toFixed(2)}</h5></div>
                                                        <div>
                                                            <button className="btn btn-sm btn-outline-primary me-1" onClick={() => handleEditClick(cn.id)}><i className="ti ti-edit"></i></button>
                                                            <button className="btn btn-sm btn-outline-danger" onClick={() => handleDeleteClick(cn.id)}><i className="ti ti-trash"></i></button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                    {/* ** FIX: Added Empty State ** */}
                                    {!viewDataLoading && notesList.data.length === 0 && (
                                        <div className="col-12">
                                            <div className="text-center p-5 text-muted border rounded bg-light">
                                                <i className="ti ti-folder-off fs-1 mb-3 d-block"></i>
                                                <h5>No Credit Notes Found</h5>
                                                <p>Click 'Add New' to create one.</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                
                              
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};
export default CreditNotesPage;